import * as React from "react";

import {
  HoverActionToolbar,
  hoverActions,
} from "@/shared/components/RobotoMarkdown/HoverActionToolbar";
import { useJumpToTimestampAction } from "@/shared/components/RobotoMarkdown/timeFormat";
import { useMessagePathById } from "@/shared/domain/topics/hooks/useMessagePath";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic";
import { QueryStatsIcon } from "@/shared/foundation/icons/mui";
import { usePublishToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  WORKSPACE_ACTION_TOPIC,
  WorkspaceActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";

import { HighlightedSpan } from "./HighlightedSpan";

interface VizInternalLinkProps {
  text: string;
  topicId?: string;
  messagePathId?: string;
  time?: bigint;
  className?: string;
}

export const VizInternalLink: React.FC<VizInternalLinkProps> = ({
  text,
  topicId,
  messagePathId,
  time,
}) => {
  // Use domain service hooks to fetch data
  const messagePathQuery = useMessagePathById(messagePathId);
  const messagePathTopicQuery = useTopicById(messagePathQuery.data?.topic_id);
  const topicQuery = useTopicById(topicId);

  const jumpAction = useJumpToTimestampAction(time);

  // Get publish function for workspace actions
  const publishWorkspaceAction =
    usePublishToAiAgentThreadPubSubBus<WorkspaceActionPayload>(
      WORKSPACE_ACTION_TOPIC,
    );

  // Determine which action is available
  const messagePath = messagePathQuery.data;
  const messagePathTopic = messagePathTopicQuery.data;
  const topic = topicQuery.data;

  const openPanelAvailable = !!messagePath && !!messagePathTopic;
  const openSearchAvailable = !!topic;

  const fullyQualifiedMessagePath = React.useMemo(() => {
    if (!messagePath || !messagePathTopic) {
      return undefined;
    }
    return `${messagePathTopic.topic_name}.${messagePath.message_path}`;
  }, [messagePathTopic, messagePath]);

  const topicName = topic?.topic_name;

  const tooltipTitle = React.useMemo(() => {
    if (openPanelAvailable) {
      return `Visualize: ${fullyQualifiedMessagePath}`;
    }
    if (openSearchAvailable) {
      return `See topic: ${topicName}`;
    }
    return "Visualization not available";
  }, [
    openPanelAvailable,
    openSearchAvailable,
    fullyQualifiedMessagePath,
    topicName,
  ]);

  // If we're still loading data, render plain text
  const isLoadingMessagePath = messagePathId && messagePathQuery.isLoading;
  const isLoadingMessagePathTopic =
    messagePathQuery.data?.topic_id && messagePathTopicQuery.isLoading;
  const isLoadingTopic = topicId && topicQuery.isLoading;

  // If no actions are available, render plain text. The jump action stands in
  // for a bare `time` here. "Set as t=0" is deliberately left to timestamp
  // renderers, even when this link carries a time.
  const hasAnyAction =
    openPanelAvailable || openSearchAvailable || jumpAction !== undefined;

  const handleOpenPanel = React.useCallback(() => {
    if (!messagePath || !messagePathTopic) {
      return false;
    }
    return publishWorkspaceAction({
      type: "open_message_path",
      payload: {
        messagePath,
        topic: messagePathTopic,
        timestamp: time,
      },
    });
  }, [messagePath, messagePathTopic, time, publishWorkspaceAction]);

  const handleOpenSearch = React.useCallback(() => {
    if (!topic) {
      return false;
    }
    return publishWorkspaceAction({
      type: "open_topic_search",
      payload: {
        topic,
        timestamp: time,
      },
    });
  }, [topic, time, publishWorkspaceAction]);

  const handleVisualizeClick = React.useCallback(() => {
    if (openPanelAvailable) {
      handleOpenPanel();
    } else if (openSearchAvailable) {
      handleOpenSearch();
    }
  }, [
    openPanelAvailable,
    openSearchAvailable,
    handleOpenPanel,
    handleOpenSearch,
  ]);

  // Early returns after all hooks have been called
  if (isLoadingMessagePath || isLoadingMessagePathTopic || isLoadingTopic) {
    return <span>{text}</span>;
  }

  if (!hasAnyAction) {
    return <span>{text}</span>;
  }

  return (
    <HoverActionToolbar
      actions={hoverActions(
        {
          key: "visualize",
          icon: <QueryStatsIcon />,
          tooltip: tooltipTitle,
          onClick: handleVisualizeClick,
          disabled: !openPanelAvailable && !openSearchAvailable,
        },
        jumpAction,
      )}
    >
      <HighlightedSpan>{text}</HighlightedSpan>
    </HoverActionToolbar>
  );
};
